import type { SimpleSearchFilter } from 'api/dashboard/widget';
import { ChannelConfiguration } from 'api/notifications';
import { ProjectFinancialsContractType } from 'api/project';

export interface ThresholdConfig {
  thresholdId: number;
  thresholdValue: number;
  notifyProjectOwners: boolean;
  notifyProjectMembers: boolean;
  notifyUsers: number[];
}

export interface UpdateFinancialAlertPayload {
  accountNotificationConfigurationMeta: {
    projectThresholdNotificationConditions: ThresholdConfig[];
  };
}

export enum FinancialNotificationEventType {
  ProjectBudgetThresholdReached = 'ProjectBudgetThresholdReached',
  ProjectHoursThresholdReached = 'ProjectHoursThresholdReached',
}

export interface FinancialNotificationConfig {
  accountId: number;
  accountNotificationConfigId: number;
  channels: ChannelConfiguration;
  createdAt: string;
  eventType: FinancialNotificationEventType;
  updatedAt: string;
  updatedBy: number;
  accountNotificationConfigurationMeta?: {
    projectThresholdNotificationConditions: ThresholdConfig[];
  };
}

export enum RevenueRecognitionProjectLevelTypes {
  AllProjects = 'ALL_PROJECT',
  ProjectLevel = 'PROJECT_LEVEL',
}

export enum FixedFeeRevenueRecognitionTypes {
  TrackedHoursByProjectHours = 'TRACKED_HOURS_BY_PROJECT_HOURS',
  TrackedHoursByAllocatedHours = 'TRACKED_HOURS_BY_ALLOCATED_HOURS',
  TrackedHoursByEstimateAtCompletion = 'TRACKED_HOURS_BY_ESTIMATE_AT_COMPLETION',
  MilestoneBased = 'MILESTONE_BASED',
  Manual = 'MANUAL',
}

export enum CustomRevenueRecognitionTypes {
  CustomRevenueRecognition = 'CUSTOM_REVENUE_RECOGNITION',
}

export enum SubscriptionRevenueRecognitionTypes {
  EndOfPeriod = 'RECOGNISE_AT_END_OF_THE_PERIOD',
  TrackedHoursByPeriodHours = 'TRACKED_HOURS_BY_PERIOD_HOURS',
  TrackedHoursByPeriodAllocatedHours = 'TRACKED_HOURS_BY_PERIOD_ALLOCATED_HOURS',
}

export enum TimeMaterialRevenueRecognitionTypes {
  AllTrackedHours = 'ALL_TRACKED_HOURS',
}

export interface RevenueRecognitionConfigApi {
  accountId: number;
  financialContractType: ProjectFinancialsContractType;
  revenueRecognitionConfigId: number;
  revenueRecognitionType:
    | FixedFeeRevenueRecognitionTypes
    | SubscriptionRevenueRecognitionTypes
    | TimeMaterialRevenueRecognitionTypes
    | CustomRevenueRecognitionTypes;
  updatedAt: string;
  allowUsersToSetRevRecAtProjectLevel: boolean;
  allowedRevenueRecognitionTypes: Record<
    | FixedFeeRevenueRecognitionTypes
    | SubscriptionRevenueRecognitionTypes
    | TimeMaterialRevenueRecognitionTypes
    | CustomRevenueRecognitionTypes,
    boolean
  >;
  defaultCustomRevenueRecognitionConfigId?: number;
  allowedCustomRevenueRecognitionConfigs?: number[];
}

export type RevenueRecognitionConfig = Record<
  ProjectFinancialsContractType,
  RevenueRecognitionConfigApi
>;

export enum CustomRevRecDenominator {
  BudgetHours = 'BUDGET_HOURS',
  EstimateAtCompletion = 'ESTIMATE_AT_COMPLETION',
  AllocatedHours = 'ALLOCATED_HOURS',
}

export interface CustomRevenueRecognitionConfigApi {
  customRevenueRecognitionConfigId: number;
  name: string;
  denominator: CustomRevRecDenominator;
  numerator: SimpleSearchFilter;
  projectsCount?: number;
}

export interface FinancialsConfiguration {
  multiLineBudgets?: boolean;
}
